import React, { useCallback } from "react"

import { Flex, Typography } from "antd"
import dayjs from "dayjs"
import ReactCountryFlag from "react-country-flag"
import { useDispatch, useSelector } from "react-redux"

import CompareSelect from "../../../../Components/Filters/CompareSelect"
import { resolveCompare } from "../../../../Components/Filters/DashboardFiltersBlock/resolveCompare"
import DropdownDateBlock from "../../../../Components/Filters/DropdownDateBlock/DropdownDateBlock"
import InfoTooltip from "../../../../Components/InfoTooltip/InfoTooltip"
import TitleSection from "../../../../Components/TitleSection/TitleSection"
import Icon from "../../../../Icon"
import { COUNTRY_CODES_MAP } from "../../../../Utils/countryCodes"
import { ensureHttps, getCalculatedPeriod } from "../../../../Utils/utils"
import HealthActions from "../../reducer"
import { getLastUpdateTimeStr } from "../../utils/getLastUpdateTimeStr"
import DataSourceSwitcher from "../DataSourceSwitcher/DataSourceSwitcher"

import "./Header.less"

const { Text } = Typography

const Header = ({ filters, handleChangeFilter, type }) => {
  const dispatch = useDispatch()
  const { isAdmin } = useSelector((state) => state.auth)
  const { businessAccount, businessAccountFetching } = useSelector((state) => state.user)
  const {
    mainInfo,
    dataSource,
    fetching: healthFetching,
    overviewFetching,
    dataByCardTypeFetching,
    dataByCountryFetching,
    dataByReasonCodeFetching,
    dataOlympicsFetching,
    riskLevelFetching
  } = useSelector((state) => state.health)

  const country = mainInfo?.country?.toUpperCase()
  const region = mainInfo?.vamp_region
  const vampUpdated = mainInfo?.sync_stripe_region_time

  const pageDescription = {
    overview: "View the health of your business.",
    monitoring: "View the health of your business."
  }

  // Region info shown in the toolbar (next to "Last updated") on monitoring.
  const regionInfo =
    type === "monitoring" ? (
      <Flex align="center" gap={4}>
        <Text type="secondary">Your region:</Text>
        {country && region ? (
          <>
            <Flex className="country-flag-container">
              <ReactCountryFlag
                countryCode={country}
                svg
                style={{ width: "20px", height: "15px" }}
                className="country-flag"
              />
              <div className="image-overlay" />
            </Flex>
            <Text type="secondary" strong>
              {COUNTRY_CODES_MAP[country]}
            </Text>
            <Text type="secondary">({region})</Text>
          </>
        ) : (
          "-"
        )}
        <InfoTooltip
          title={
            region
              ? "Maintaining a low chargeback rate helps ensure uninterrupted payment processing and lowers your risk level."
              : "Sorry for the inconvenience, the data will be updated soon"
          }
        />
      </Flex>
    ) : null

  const videoUrl = {
    overview: null,
    monitoring: null
  }

  const pageTitles = {
    overview: "Health overview",
    monitoring: "Health monitoring"
  }

  const pageData = {
    title: isAdmin ? `Health for ${businessAccount?.name}` : pageTitles[type],
    description: pageDescription[type],
    buttonText: videoUrl[type] ? "Watch tutorial" : null,
    videoUrl: videoUrl[type]
  }

  const lastUpdatedDate =
    type === "monitoring" ? mainInfo?.sync_stripe_region_time : mainInfo?.last_update

  const lastUpdatedTimeString = lastUpdatedDate
    ? getLastUpdateTimeStr(lastUpdatedDate)
    : "Never updated"

  const isSwitcherLoading =
    businessAccountFetching ||
    healthFetching ||
    overviewFetching ||
    dataByCardTypeFetching ||
    dataByCountryFetching ||
    dataByReasonCodeFetching ||
    dataOlympicsFetching ||
    riskLevelFetching

  const switchDataSource = (value) => {
    dispatch(HealthActions.healthSetProp("dataSource", value))
  }

  const handleChangeCustomDate = useCallback(
    (newFilters) => {
      if (!newFilters?.period) {
        setTimeout(() => handleChangeFilter({ period: "27 days" }), 100)
        return
      }

      const nextPeriod = newFilters.period
      const wasAllTime = filters?.period === "all"
      const goingToAllTime = nextPeriod === "all"

      if (goingToAllTime) {
        handleChangeFilter({
          ...newFilters,
          lastCompare: filters?.compare || filters?.lastCompare || null,
          compare: null
        })
        return
      }

      const sourceCompare = wasAllTime ? filters?.lastCompare : filters?.compare

      handleChangeFilter({
        ...newFilters,
        compare: resolveCompare(sourceCompare, nextPeriod),
        lastCompare: wasAllTime ? null : filters?.lastCompare
      })
    },
    [filters?.period, filters?.compare, filters?.lastCompare, handleChangeFilter]
  )

  const compareCurrentValue = React.useMemo(() => {
    const calculated = getCalculatedPeriod(filters?.period)
    if (!calculated) return null
    const [from, to] = calculated
    return {
      preset: filters?.period?.type === "custom" ? "custom" : "relative",
      range: [
        dayjs.isDayjs(from) ? from.format("YYYY-MM-DD") : from,
        dayjs.isDayjs(to) ? to.format("YYYY-MM-DD") : to
      ]
    }
  }, [filters?.period])

  return (
    <>
      <TitleSection
        icon="centralhub"
        title={pageData.title}
        description={
          type !== "monitoring" && isAdmin ? (
            <a href={ensureHttps(businessAccount?.url)} target="_blank" rel="noopener noreferrer">
              {ensureHttps(businessAccount?.url)}
            </a>
          ) : (
            pageData.description
          )
        }
        buttonText={pageData.buttonText}
        videoUrl={pageData.videoUrl}
        style={{ margin: 0, width: "100%" }}
        className="health-header"
      />
      <Flex className="health-toolbar" align="center" justify="space-between" gap={12} wrap>
        <Flex gap={16} align="center" wrap>
          <Flex gap={7} align="center">
            <Icon type="arrows_circle" style={{ color: "#35BD29", fontSize: "16px" }} />
            <Text type="secondary" strong>{`Last updated: ${lastUpdatedTimeString}`}</Text>
          </Flex>
          {regionInfo}
        </Flex>
        <Flex align="center" gap={12} wrap className="health-filters-container">
          <DropdownDateBlock
            id={"period"}
            customOverlayClassName={"health-header-date-filter"}
            label={"Custom"}
            iconType={"calendar_days"}
            setDropdownFilters={handleChangeCustomDate}
            dropdownFilters={getCalculatedPeriod(filters?.period)}
            presetId={filters?.__preset_period}
            inactive={false}
            alwaysShowLabel={false}
          />
          <CompareSelect
            currentValue={compareCurrentValue}
            value={filters?.compare}
            onChange={(val) => handleChangeFilter({ compare: val })}
            disabled={filters?.period === "all"}
          />
          {type === "overview" && (
            <DataSourceSwitcher
              onSwitch={switchDataSource}
              dataSource={dataSource}
              loading={isSwitcherLoading}
            />
          )}
        </Flex>
      </Flex>
    </>
  )
}

export default Header
